import React, { useMemo, useState } from "react";
import {
  LayoutGrid,
  MousePointerClick,
  Eye,
  TrendingUp,
  Link2,
  BarChart3,
  Sparkles,
} from "lucide-react";
import { isInFilter } from "../utils/dateFilter";

const FILTER_TABS = ["Today", "Yesterday", "Weekly", "Monthly", "All Time", "Custom Date"];

function getFilterLabel(filter) {
  if (typeof filter === "string") return filter;
  if (filter?.type === "Custom Date" && filter.from && filter.to) {
    return `${filter.from} to ${filter.to}`;
  }
  return "Selected period";
}

function menuMetric(item, key) {
  return Number(item?.[key]) || 0;
}

function buildSummaryFromMenus(menuItems, period, megaMenuCount = 0) {
  const periodMenus = (Array.isArray(menuItems) ? menuItems : []).filter((item) => {
    const created = item?.createdAt ? new Date(item.createdAt) : null;
    if (!created || isNaN(created.getTime())) return period === "All Time";
    return isInFilter(created, period);
  });

  const byMenu = periodMenus
    .map((item) => {
      const clicks = menuMetric(item, "clicks");
      const visits = menuMetric(item, "visits");
      return {
        menuId: item.id,
        label: item.label || "Untitled",
        clicks,
        visits,
        megaMenuClicks: 0,
        megaMenuVisits: 0,
        totalClicks: clicks,
        totalVisits: visits,
      };
    })
    .sort((a, b) => b.totalClicks - a.totalClicks || b.totalVisits - a.totalVisits);

  const totalClicks = byMenu.reduce((sum, row) => sum + row.totalClicks, 0);
  const totalVisits = byMenu.reduce((sum, row) => sum + row.totalVisits, 0);
  const clickRate = totalVisits > 0 ? Math.round((totalClicks / totalVisits) * 100) : 0;
  const menusWithActivity = byMenu.filter(
    (row) => row.totalClicks > 0 || row.totalVisits > 0
  ).length;

  return {
    filterLabel: getFilterLabel(period),
    menuCount: periodMenus.length,
    megaMenuCount,
    totalClicks,
    totalVisits,
    clickRate,
    menusWithActivity,
    menusWithoutActivity: Math.max(0, periodMenus.length - menusWithActivity),
    byMenu,
    topByClicks: byMenu.slice(0, 5),
  };
}

const SummaryDateFilter = ({ value, onChange }) => {
  const [activeTab, setActiveTab] = useState(
    typeof value === "string" ? value : value?.type === "Custom Date" ? "Custom Date" : "All Time"
  );
  const [showCustom, setShowCustom] = useState(activeTab === "Custom Date");
  const [tempRange, setTempRange] = useState({
    from: value?.from || "",
    to: value?.to || "",
  });

  const handleTabClick = (tab) => {
    if (tab === "Custom Date") {
      setActiveTab("Custom Date");
      setShowCustom(true);
      return;
    }
    setActiveTab(tab);
    setShowCustom(false);
    onChange(tab);
  };

  const handleApplyCustom = () => {
    if (!tempRange.from || !tempRange.to || tempRange.from > tempRange.to) return;
    onChange({ type: "Custom Date", from: tempRange.from, to: tempRange.to });
    setShowCustom(false);
  };

  return (
    <div className="menus-summary__date-filter">
      <div className="menus-summary__date-pills dash-filter-scroll">
        {FILTER_TABS.map((tab) => {
          const isActive = activeTab === tab;
          return (
            <button
              key={tab}
              type="button"
              onClick={() => handleTabClick(tab)}
              className={`menus-summary__date-pill${isActive ? " menus-summary__date-pill--active" : ""}`}
            >
              {tab}
            </button>
          );
        })}
      </div>

      {showCustom && (
        <div className="menus-summary__custom-range">
          <div className="menus-summary__custom-fields">
            <label className="menus-summary__custom-label">
              From
              <input
                type="date"
                value={tempRange.from}
                onChange={(e) => setTempRange((prev) => ({ ...prev, from: e.target.value }))}
                className="menus-summary__custom-input"
              />
            </label>
            <label className="menus-summary__custom-label">
              To
              <input
                type="date"
                value={tempRange.to}
                onChange={(e) => setTempRange((prev) => ({ ...prev, to: e.target.value }))}
                className="menus-summary__custom-input"
              />
            </label>
          </div>
          <div className="menus-summary__custom-actions">
            <button
              type="button"
              onClick={() => {
                setShowCustom(false);
                if (typeof value === "string") setActiveTab(value);
              }}
              className="menus-summary__custom-btn menus-summary__custom-btn--ghost"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleApplyCustom}
              disabled={!tempRange.from || !tempRange.to || tempRange.from > tempRange.to}
              className="menus-summary__custom-btn menus-summary__custom-btn--primary"
            >
              Apply
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

const MetricCard = ({ label, value, hint, tone = "default", icon: Icon }) => {
  const toneClass =
    tone === "success"
      ? "menus-summary__metric-value--success"
      : tone === "warn"
        ? "menus-summary__metric-value--warn"
        : tone === "danger"
          ? "menus-summary__metric-value--danger"
          : "";

  return (
    <div className="menus-summary__metric-card">
      <div className="menus-summary__metric-head">
        <p className="menus-summary__metric-label">{label}</p>
        {Icon ? <Icon size={14} className="menus-summary__metric-icon" /> : null}
      </div>
      <p className={`menus-summary__metric-value ${toneClass}`}>{value}</p>
      {hint ? <p className="menus-summary__metric-hint">{hint}</p> : null}
    </div>
  );
};

export default function MenuPageSummary({
  menuItems = [],
  megaMenuCount = 0,
  period = "All Time",
  onPeriodChange,
  onHighlightMenuId,
  highlightMenuId = null,
}) {
  const data = useMemo(
    () => buildSummaryFromMenus(menuItems, period, megaMenuCount),
    [menuItems, period, megaMenuCount]
  );

  return (
    <section className="menus-page-summary">
      <div className="menus-summary__header">
        <div>
          <h2 className="menus-summary__title">Menu Analytics</h2>
          <p className="menus-summary__subtitle">
            Clicks and visits across all menu links — {data.filterLabel}
          </p>
        </div>
        <SummaryDateFilter value={period} onChange={onPeriodChange} />
      </div>

      <div className="menus-summary__grid">
        <div className="menus-summary__panel menus-summary__panel--engagement">
          <div className="menus-summary__panel-head">
            <div className="menus-summary__panel-icon menus-summary__panel-icon--violet">
              <MousePointerClick size={16} />
            </div>
            <h3 className="menus-summary__panel-title">Engagement</h3>
          </div>
          <div className="menus-summary__metrics dash-metric-scroll menus-metric-scroll">
            <MetricCard
              label="Total Clicks"
              value={data.totalClicks}
              hint="Menu + mega menu taps"
              icon={MousePointerClick}
              tone={data.totalClicks > 0 ? "success" : "default"}
            />
            <MetricCard
              label="Total Visits"
              value={data.totalVisits}
              hint="Menu impressions"
              icon={Eye}
              tone={data.totalVisits > 0 ? "warn" : "default"}
            />
            <MetricCard
              label="Click Rate"
              value={`${data.clickRate}%`}
              hint="Clicks per visit"
              icon={TrendingUp}
            />
            <MetricCard
              label="Active Menus"
              value={data.menusWithActivity}
              hint={`${data.menusWithoutActivity} with no activity`}
              icon={Sparkles}
            />
          </div>
        </div>

        <div className="menus-summary__panel menus-summary__panel--inventory">
          <div className="menus-summary__panel-head">
            <div className="menus-summary__panel-icon menus-summary__panel-icon--indigo">
              <LayoutGrid size={16} />
            </div>
            <h3 className="menus-summary__panel-title">Inventory</h3>
          </div>
          <div className="menus-summary__metrics dash-metric-scroll menus-metric-scroll">
            <MetricCard
              label="Menu Items"
              value={data.menuCount}
              hint="Top-level links"
              icon={LayoutGrid}
            />
            <MetricCard
              label="Mega Menu Links"
              value={data.megaMenuCount}
              hint="Sub-menu entries"
              icon={Link2}
            />
            <MetricCard
              label="Tracked Events"
              value={data.totalClicks + data.totalVisits}
              hint="Clicks + visits"
              icon={BarChart3}
            />
          </div>
        </div>
      </div>

      <div className="menus-summary__breakdown">
        <p className="menus-summary__breakdown-title">Clicks vs visits</p>
        <div className="menus-summary__breakdown-bar">
          {data.totalVisits > 0 && (
            <span
              className="menus-summary__breakdown-segment menus-summary__breakdown-segment--visits"
              style={{
                width: `${Math.max(
                  4,
                  Math.round(
                    (data.totalVisits / Math.max(data.totalVisits + data.totalClicks, 1)) * 100
                  )
                )}%`,
              }}
              title={`Visits: ${data.totalVisits}`}
            />
          )}
          {data.totalClicks > 0 && (
            <span
              className="menus-summary__breakdown-segment menus-summary__breakdown-segment--clicks"
              style={{
                width: `${Math.max(
                  4,
                  Math.round(
                    (data.totalClicks / Math.max(data.totalVisits + data.totalClicks, 1)) * 100
                  )
                )}%`,
              }}
              title={`Clicks: ${data.totalClicks}`}
            />
          )}
        </div>
        <div className="menus-summary__breakdown-legend">
          <span>
            <i className="menus-summary__dot menus-summary__dot--visits" />
            Visits ({data.totalVisits})
          </span>
          <span>
            <i className="menus-summary__dot menus-summary__dot--clicks" />
            Clicks ({data.totalClicks})
          </span>
        </div>
      </div>

      {data.byMenu.length > 0 && (
        <div className="menus-summary__table-wrap">
          <p className="menus-summary__table-title">Per-menu performance</p>
          <div className="menus-summary__table-scroll">
            <table className="menus-summary__table">
              <thead>
                <tr>
                  <th>Menu</th>
                  <th>Visits</th>
                  <th>Clicks</th>
                  <th>CTR</th>
                </tr>
              </thead>
              <tbody>
                {data.byMenu.map((row) => {
                  const ctr =
                    row.totalVisits > 0
                      ? Math.round((row.totalClicks / row.totalVisits) * 100)
                      : 0;
                  const isActive = highlightMenuId === row.menuId;
                  return (
                    <tr
                      key={row.menuId ?? row.label}
                      className={isActive ? "menus-summary__table-row--active" : ""}
                      onClick={() => onHighlightMenuId?.(row.menuId)}
                    >
                      <td>{row.label}</td>
                      <td>{row.totalVisits}</td>
                      <td>{row.totalClicks}</td>
                      <td>{ctr}%</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <div className="menus-summary__insights">
        <div className="menus-summary__insight">
          <Eye size={14} />
          <span>
            {data.totalVisits > 0
              ? `${data.totalVisits} visit(s) recorded across your menus in this period.`
              : "No menu visits tracked yet. Visits are logged when customers open a menu on your storefront."}
          </span>
        </div>
        <div className="menus-summary__insight">
          <MousePointerClick size={14} />
          <span>
            {data.totalClicks > 0
              ? `${data.totalClicks} click(s) — ${data.clickRate}% click-through rate.`
              : "No menu clicks tracked yet. Clicks are logged when customers tap a menu link."}
          </span>
        </div>
        <div className="menus-summary__insight">
          <LayoutGrid size={14} />
          <span>
            {data.topByClicks[0]
              ? `Top menu: "${data.topByClicks[0].label}" with ${data.topByClicks[0].totalClicks} click(s).`
              : `${data.menuCount} menu item(s) configured — analytics will appear as traffic is tracked.`}
          </span>
        </div>
      </div>
    </section>
  );
}
